import { useCallback, useRef, useState, type ReactNode } from 'react';
import { isoDateInZone, type PomodoroMode } from '@cleartask/shared';
import { useTimeZone } from '../../session.js';
import { useTaskList } from '../tasks/useTasks.js';
import { formatClock, usePomodoro, usePomodoroStats } from './usePomodoro.js';

const RAIO = 84;
const CIRCUNFERENCIA = 2 * Math.PI * RAIO;

const MODE_LABEL: Record<PomodoroMode, string> = {
  focus: 'Foco',
  short_break: 'Pausa curta',
  long_break: 'Pausa longa',
  stopwatch: 'Cronômetro',
};

interface TimerDialProps {
  /** Texto central, ja formatado. */
  display: string;
  /** 0..1. Um anel cheio o tempo todo no cronometro nao diria nada. */
  progress: number;
  label: string;
  children?: ReactNode;
}

/**
 * Anel de progresso com o digito no centro (spec 9.3).
 *
 * O digito usa `JetBrains Mono` com `tabular-nums` (spec 7.2): sem largura
 * fixa, os algarismos mudam de largura a cada segundo e o numero inteiro
 * "pula" na tela — o tipo de ruido que se percebe sem saber explicar.
 */
export function TimerDial({ display, progress, label, children }: TimerDialProps) {
  return (
    <div className="relative grid place-items-center">
      <svg width="200" height="200" viewBox="0 0 200 200" aria-hidden="true">
        <circle cx="100" cy="100" r={RAIO} fill="none" stroke="var(--surface-2)" strokeWidth="8" />
        <circle
          cx="100"
          cy="100"
          r={RAIO}
          fill="none"
          stroke="var(--brand-blue)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={CIRCUNFERENCIA}
          strokeDashoffset={CIRCUNFERENCIA * (1 - progress)}
          transform="rotate(-90 100 100)"
          style={{ transition: 'stroke-dashoffset var(--dur) linear' }}
        />
      </svg>

      <div className="absolute flex flex-col items-center gap-1">
        <span className="text-sm text-text-mut">{label}</span>
        <span className="font-mono text-2xl font-medium tabular-nums" role="timer">
          {display}
        </span>
        {children}
      </div>
    </div>
  );
}

/** Pomodoro (spec 9.3). */
export function PomodoroTimer() {
  const timeZone = useTimeZone();
  const { session, remainingS, progress, durations, cyclesBeforeLongBreak, start, stop } =
    usePomodoro(useCallback(() => notaRef.current, []));

  const [taskId, setTaskId] = useState<string>('');
  const [nota, setNota] = useState('');

  // A nota tambem e lida no encerramento AUTOMATICO do ciclo, de dentro de um
  // efeito. Um valor de estado capturado ali ficaria congelado; a ref sempre
  // devolve o que esta na caixa agora.
  const notaRef = useRef(nota);
  notaRef.current = nota;
  const { data: hoje } = useTaskList({ view: 'today' });
  const { data: stats } = usePomodoroStats('week');

  const emAndamento = session !== null;
  const modo: PomodoroMode = session?.mode ?? 'focus';

  const doDia = stats?.stats.byDay.find((d) => d.date === isoDateInZone(timeZone, Date.now()));
  const pomosHoje = doDia?.pomos ?? 0;

  // Ciclo dentro do bloco atual: cumpridos 4 focos, vem a pausa longa.
  const cicloAtual = pomosHoje % cyclesBeforeLongBreak;
  const proximaPausa: PomodoroMode =
    cicloAtual === cyclesBeforeLongBreak - 1 ? 'long_break' : 'short_break';

  const display = emAndamento ? formatClock(remainingS) : formatClock(durations.focus);
  const tarefaVinculada = hoje?.tasks.find((t) => t.id === session?.taskId);

  return (
    <div className="flex flex-col items-center gap-5">
      <TimerDial display={display} progress={progress} label={MODE_LABEL[modo]}>
        <div className="flex gap-1.5 pt-1" aria-label={`${pomosHoje} pomodoros hoje`}>
          {Array.from({ length: cyclesBeforeLongBreak }, (_, i) => (
            <span
              key={i}
              aria-hidden="true"
              className={[
                'h-2 w-2 rounded-full',
                i < cicloAtual ? 'bg-brand-blue' : 'bg-surface-2',
              ].join(' ')}
            />
          ))}
        </div>
      </TimerDial>

      {emAndamento ? (
        <p className="text-sm text-text-mut">
          {tarefaVinculada ? tarefaVinculada.title : 'Sem tarefa vinculada'}
        </p>
      ) : (
        <div className="flex w-full max-w-xs flex-col gap-1">
          <label htmlFor="focus-task" className="text-sm text-text-mut">
            Vincular a uma tarefa
          </label>
          <select
            id="focus-task"
            value={taskId}
            onChange={(event) => setTaskId(event.target.value)}
            className="min-h-touch rounded border border-border bg-surface px-3 text-base"
          >
            <option value="">Sem tarefa</option>
            {(hoje?.tasks ?? []).map((task) => (
              <option key={task.id} value={task.id}>
                {task.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {emAndamento ? (
        /*
          A nota pertence a SESSAO, nao a tarefa: ela descreve onde ESTE bloco
          parou ("faltou o parecer do Jorge"), e gravar em `tasks.notes`
          sobrescreveria o campo a cada ciclo — apagando justamente o historico
          que faz a anotacao valer.

          Fica visivel durante o foco, e nao numa caixa no fim: a ideia que vale
          anotar aparece no meio do trabalho, e quem tem de parar para abrir um
          formulario ja perdeu o pensamento.
        */
        <div className="flex w-full max-w-xs flex-col gap-1">
          <label htmlFor="focus-note" className="text-sm text-text-mut">
            Anotações deste bloco
          </label>
          <textarea
            id="focus-note"
            value={nota}
            onChange={(event) => setNota(event.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Onde você parou, o que travou…"
            className="resize-y rounded border border-border bg-surface p-2 text-sm"
          />
        </div>
      ) : null}

      {emAndamento ? (
        /*
          "Abandonar" grava a sessao com `completed = 0`: o tempo focado conta,
          o ciclo nao. Descartar a sessao inteira apagaria minutos de trabalho
          que existiram de verdade.
        */
        <button
          type="button"
          onClick={() => stop(false, nota)}
          className="min-h-touch rounded border border-border px-4 text-sm hover:bg-surface-2"
        >
          Abandonar
        </button>
      ) : (
        <div className="flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => start('focus', taskId === '' ? null : taskId)}
            className="min-h-touch rounded bg-brand-blue px-6 font-medium text-white transition-colors duration-150 hover:bg-brand-blue-hover"
          >
            Começar
          </button>

          <button
            type="button"
            onClick={() => start(proximaPausa, null)}
            className="min-h-touch rounded border border-border px-4 text-sm hover:bg-surface-2"
          >
            {proximaPausa === 'long_break' ? 'Pausa longa' : 'Pausa curta'}
          </button>
        </div>
      )}
    </div>
  );
}
